import * as Sentry from '@sentry/react';
import Button from 'components/base/Button';
import { Card } from 'react-bootstrap';
import type { ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Shows up in Sentry as tag `errorBoundary` */
  name: string;
};

/**
 * Captures React render errors to Sentry and shows a local fallback. Use for page shells (`MainLayout`)
 * or wrap individual features (e.g. dashboard panels).
 */
export function SentryErrorBoundary({ children, name }: Props) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        scope.setTag('errorBoundary', name);
        scope.setContext('react', { errorBoundary: name });
      }}
      fallback={({ error, resetError, eventId }) => (
        <div className="p-4">
          <Card body className="border-danger">
            <h4 className="h5 text-danger mb-2">This section crashed</h4>
            <p className="text-body-secondary small mb-2">
              {error instanceof Error ? error.message : 'An unexpected error occurred.'}
            </p>
            {eventId ? (
              <p className="text-muted small mb-3">
                Reference: <code>{eventId}</code>
              </p>
            ) : null}
            <Button variant="primary" size="sm" onClick={resetError}>
              Try again
            </Button>
          </Card>
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
